import * as t from 'io-ts';
import _ from 'lodash';

import {
  ActivityVisualTypesEnum,
  BasicVisualTypesEnum,
  computeActivityVisualTypeFromType,
  computeBasicActivityVisualType,
  parseActivityType,
} from './activityModel';
import {makeApiType, RTopicIdRef} from './apiTypes';
import {RPreviewerLinkTypesEnum} from './previewerModel';

/*
 * Runtime type.
 */

const RTopicDirty = t.readonly(
  t.type({
    id: t.number,
    alias: t.string,
    url: t.string,
    type: t.string,
    name: t.string,
    description: t.union([t.string, t.undefined]),
    status: t.union([t.string, t.undefined]),
    ownerId: t.union([t.number, t.undefined]),
    appLink: t.string,
    externalLink: t.string,
    externalLinkType: t.union([RPreviewerLinkTypesEnum, t.undefined]),
    updatedAt: t.Integer,
  }),
);

/*
 * Type.
 */

export interface TopicModel extends t.TypeOf<typeof RTopicDirty> {}
export const RTopic = t.clean<TopicModel>(RTopicDirty as any);
RTopicIdRef.setTargetType(RTopic);

/*
 * IO methods.
 */

export function parseTopic(src: any): TopicModel {
  return {
    id: src.id,
    alias: src.alias,
    url: src.url,
    type: src.type,
    name: src.name,
    description: src.description ?? undefined,
    status: src.status ?? undefined,
    ownerId: src.owner_id ?? undefined,
    appLink: src.link,
    externalLink: src.ext_link,
    externalLinkType: src.ext_link_type ?? undefined,
    updatedAt: src.updated_at ?? 0,
  };
}

export function parseTopicsFromConversation(src: any): ReadonlyArray<TopicModel> {
  return _(src.topics)
    .compact()
    .map((topic) => parseTopic(topic))
    .value();
}

export function parseTopicFromActivity(src: any): TopicModel | undefined {
  const type = parseActivityType(src.type);
  const visualType = computeActivityVisualTypeFromType(type);

  // We will only process basic activity types.
  if (visualType !== ActivityVisualTypesEnum.BASIC) {
    return undefined;
  }

  const basicVisualType = computeBasicActivityVisualType(src);

  // We will only process topic basic visual types.
  if (basicVisualType !== BasicVisualTypesEnum.TOPIC) {
    return undefined;
  }

  return parseTopic(src.topic);
}

/*
 * API type.
 */

export const ApiTopic = makeApiType('topic', RTopic, parseTopic);
